import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';
import { GraciasPage } from './pages/GraciasPage';
import { GuiaPage } from './pages/GuiaPage';
import { PedidoPage } from './pages/PedidoPage';
import { FotoPage } from './pages/FotoPage';
import { RevisionPage } from './pages/RevisionPage';
import { AppointmentPage } from './pages/AppointmentPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-0" data-mp="34nabp7octvk">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-1" data-mp="3glwc9x83idvj">
      <BrowserRouter data-id="element-2" data-mp="3gja887keygi2">
        <ScrollToTop data-id="element-3" data-mp="3bmjqzgwvhev2" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-4" data-mp="3bn2z65wq0ovw">
          <SkipLink data-id="element-5" data-mp="3qbfvj3zq09qx" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-6" data-mp="2xtc0jskj2pey" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-7" data-mp="2svjwy1aiteux">
            <Header data-id="element-8" data-mp="18d9ds8p0zot8" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-9" data-mp="18czpcpjmpppn">
            <Routes data-id="element-10" data-mp="1d6aywyftskl9">
              <Route path="/" element={<Home data-id="element-12" data-mp="ygmsxjwp8xr7" />} data-id="element-11" data-mp="yg3nh2o8k1u2" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-14" data-mp="13eof4dpf9d6w" />} data-id="element-13" data-mp="yhern3llfblp" />
              <Route path="/pricing" element={<PricingPage data-id="element-16" data-mp="13ey726u11uf8" />} data-id="element-15" data-mp="13fpzx9w8pe1t" />
              <Route path="/faq" element={<FAQPage data-id="element-18" data-mp="1r5aktybyeldq" />} data-id="element-17" data-mp="1r4ioumeg2lmx" />
              <Route path="/contact" element={<Contact data-id="element-20" data-mp="1w3c2cetqecn3" />} data-id="element-19" data-mp="1r5276480bc1h" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-22" data-mp="1w2kj83hbm7dp" />} data-id="element-21" data-mp="1w3ltw6ufcruw" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-24" data-mp="1i5ngb3nxgzgg" />} data-id="element-23" data-mp="1i3ulclmog3oa" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-26" data-mp="1m3cwipop50dx" />} data-id="element-25" data-mp="1m4efdgbyer67" />
              <Route path="/gracias" element={<GraciasPage data-id="element-28" data-mp="6ncqur2n4b4a" />} data-id="element-27" data-mp="6onqcep1ywpl" />
              <Route path="/guia" element={<GuiaPage data-id="element-30" data-mp="apr6z3excwrv" />} data-id="element-29" data-mp="6qictxfz2wvs" />
              <Route path="/pedido" element={<PedidoPage data-id="element-32" data-mp="arc14rxbudp7" />} data-id="element-31" data-mp="ar296ysq1wgv" />
              <Route path="/foto" element={<FotoPage data-id="element-34" data-mp="qxf6pn3jmxgp" />} data-id="element-33" data-mp="qx5h40meusbj" />
              <Route path="/revision" element={<RevisionPage data-id="element-36" data-mp="qyym7bhdl4hf" />} data-id="element-35" data-mp="qyqahmwzo57t" />
              <Route path="/appointment" element={<AppointmentPage data-id="element-38" data-mp="m548coogbjq9" />} data-id="element-37" data-mp="m65imqxn826x" />
            </Routes>
          </main>
          <Footer data-id="element-39" data-mp="h8drw08qim4b" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-40" data-mp="c8rfft0qd9wk" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}